import React, { useEffect, useState } from 'react';
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { ComparisonJudgeConfig } from '../../../content/types';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function ComparisonJudgeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = (question.data?.comparisonJudge || question.data || {}) as ComparisonJudgeConfig;
  const items = config.items || [];
  const criterion = config.criterion || '';
  const correctIndex = config.correctIndex ?? -1;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  useEffect(() => {
    setSelectedIndex(null);
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null);
  }, [selectedIndex, onReadyChange]);
  // Hook only fires when shouldValidate rises; -1 fallback is gated by onReady.
  useEdgeTriggeredSubmit(selectedIndex ?? -1, shouldValidate, onAnswer, question.id);
  const handleItemClick = (index: number) => {
    if (shouldValidate) return;
    // Toggle: clicking the active item deselects it.
    setSelectedIndex(prev => prev === index ? null : index);
  };
  if (items.length < 2) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-10387">
        <p className="text-red-700 font-bold" data-id="element-10388">
          ⚠️ Configuratie ontbreekt (minimaal 2 items vereist)
        </p>
      </div>;
  }
  return <div className="w-full max-w-4xl mx-auto flex flex-col gap-6" data-id="element-10389">
      {criterion && <div className="flex justify-center" data-id="element-10390">
          <div className="bg-slate-800 text-white px-5 py-2 sm:px-6 sm:py-2.5 rounded-full font-bold text-sm sm:text-base shadow-md whitespace-nowrap" data-id="element-10391">
            {criterion}
          </div>
        </div>}
      <div className="flex flex-row gap-4 sm:gap-6 justify-center items-stretch" data-id="element-10392">
        {items.map((item, index) => {
        const isSelected = selectedIndex === index;
        let validationClass = 'bg-white border-slate-200 text-slate-700';
        let ValidationIcon = null;
        let customStyle: React.CSSProperties | undefined = undefined;
        if (shouldValidate) {
          const isCorrect = index === correctIndex;
          if (isSelected) {
            validationClass = isCorrect ? 'bg-emerald-50 border-emerald-500 text-emerald-900 ring-4 ring-emerald-100' : 'bg-red-50 border-red-500 text-red-900 ring-4 ring-red-100';
            ValidationIcon = isCorrect ? <CheckCircle2 className="w-8 h-8 text-emerald-500 absolute top-4 right-4" data-id="element-10393" /> : <XCircle className="w-8 h-8 text-red-500 absolute top-4 right-4" data-id="element-10394" />;
          } else if (isCorrect) {
            validationClass = 'bg-emerald-50/50 border-emerald-300 text-emerald-800';
            ValidationIcon = <CheckCircle2 className="w-8 h-8 text-emerald-400 absolute top-4 right-4" data-id="element-10395" />;
          } else {
            validationClass = 'bg-slate-50 border-slate-200 text-slate-400 opacity-50';
          }
        } else if (isSelected) {
          validationClass = 'scale-105';
          customStyle = {
            backgroundColor: hexToRgba(color, 0.05),
            borderColor: color,
            color: color,
            '--tw-ring-color': hexToRgba(color, 0.15),
            '--tw-ring-offset-width': '0px',
            '--tw-ring-offset-color': '#fff',
            '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
            '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
            boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000'
          };
        }
        return <motion.button key={index} onClick={() => handleItemClick(index)} disabled={shouldValidate} whileHover={!shouldValidate && !isSelected ? {
          scale: 1.02,
          y: -4
        } : {}} whileTap={!shouldValidate ? {
          scale: 0.98
        } : {}} className={`flex-1 flex flex-col items-center justify-center p-6 sm:p-8 rounded-3xl border-4 transition-all duration-300 relative overflow-hidden ${shouldValidate ? 'cursor-default' : 'cursor-pointer hover:shadow-lg'} ${validationClass}`} style={customStyle} onMouseEnter={e => {
          if (!isSelected && !shouldValidate) {
            e.currentTarget.style.borderColor = hexToRgba(color, 0.3);
          }
        }} onMouseLeave={e => {
          if (!isSelected && !shouldValidate) {
            e.currentTarget.style.borderColor = '';
          }
        }} data-id="element-10396">
              {ValidationIcon}

              <div className="text-4xl sm:text-6xl md:text-7xl mb-4 sm:mb-6 select-none transform transition-transform duration-300 group-hover:scale-110" data-id="element-10397">
                {item.emoji}
              </div>

              <div className="text-xl sm:text-2xl md:text-3xl font-black text-center break-words w-full" data-id="element-10398">
                {item.label}
              </div>

              {item.subLabel && <div className="text-sm sm:text-base font-medium opacity-70 mt-2 text-center" data-id="element-10399">
                  {item.subLabel}
                </div>}
            </motion.button>;
      })}
      </div>
    </div>;
}